import { useEffect, useRef } from 'react';

/**
 * Check if all required Firebase Web config environment variables are available
 */
function hasFirebaseWebConfig(): boolean {
  // These would be set in .env as VITE_FIREBASE_* variables
  const requiredVars = [
    import.meta.env.VITE_FIREBASE_API_KEY,
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    import.meta.env.VITE_FIREBASE_PROJECT_ID,
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    import.meta.env.VITE_FIREBASE_APP_ID,
    import.meta.env.VITE_FIREBASE_VAPID_KEY,
  ];

  return requiredVars.every((v) => v && v.length > 0);
}

/**
 * Guarded hook for web push notification token registration.
 * 
 * This hook will only initialize Firebase Messaging and register tokens
 * when the required Firebase Web app config and VAPID key are available
 * in the environment variables AND the Firebase packages are installed.
 * 
 * If config is missing or packages not installed, the hook is a no-op (safe to call).
 * 
 * Required environment variables:
 * - VITE_FIREBASE_API_KEY
 * - VITE_FIREBASE_AUTH_DOMAIN
 * - VITE_FIREBASE_PROJECT_ID
 * - VITE_FIREBASE_MESSAGING_SENDER_ID
 * - VITE_FIREBASE_APP_ID
 * - VITE_FIREBASE_VAPID_KEY
 * 
 * Required npm packages (install when ready to enable):
 * - firebase
 */
export function useWebNotificationToken(isAuthenticated: boolean) {
  const isInitializedRef = useRef(false);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Guard: only run if user is authenticated
    if (!isAuthenticated) {
      return;
    }

    // Guard: check if already initialized
    if (isInitializedRef.current) {
      return;
    }

    // Guard: check if Firebase config is available
    // If not, skip entirely (no console log to avoid noise)
    if (!hasFirebaseWebConfig()) {
      return;
    }

    // Async initialization wrapped in IIFE
    (async () => {
      try {
        // Dynamic imports - these will fail if firebase package isn't installed
        // That's expected and handled by the try-catch
        const { apiClient } = await import('@/lib/api-client');
        const { API_ROUTES } = await import('@mangelapp/shared');
        const { initializeApp, getApps } = await import('firebase/app');
        const { getMessaging, getToken, onMessage } = await import('firebase/messaging');

        // Initialize Firebase if not already done
        const firebaseConfig = {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
          appId: import.meta.env.VITE_FIREBASE_APP_ID,
        };

        const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
        const messaging = getMessaging(app);

        // Request permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.log('[FCM Web] Notification permission denied');
          return;
        }

        // Get token
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
        const token = await getToken(messaging, { vapidKey });
        
        if (token) {
          tokenRef.current = token;
          
          // Register with backend
          try {
            await apiClient.post(API_ROUTES.NOTIFICATIONS.REGISTER_TOKEN, {
              token,
              platform: 'web',
              deviceName: navigator.userAgent.substring(0, 100),
            });
            console.log('[FCM Web] Token registered with backend');
          } catch (regError) {
            console.error('[FCM Web] Failed to register token:', regError);
          }
          
          isInitializedRef.current = true;

          // Listen for foreground messages
          onMessage(messaging, (payload) => {
            console.log('[FCM Web] Message received:', payload);
          });
        }
      } catch {
        // Firebase packages not installed or other error - this is expected
        // when web push is not yet configured. Silent fail.
      }
    })();
  }, [isAuthenticated]);

  return {
    token: tokenRef.current,
    isSupported: 'Notification' in window && hasFirebaseWebConfig(),
  };
}
